import type { ReactNode } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { getCanonicalUrl } from '@/config/site';
import PublicPageTemplate from '@/components/public/PublicPageTemplate';
import GenieEmbed from '@/components/widget/GenieEmbed';

const CONTACT_EMAIL = "support@helpgenie.ai";

const Bullet = ({ children }: { children: ReactNode }) => (
  <li className="flex gap-3">
    <span className="text-purple-600 font-bold">•</span>
    <span>{children}</span>
  </li>
);

const PolicyCard = ({ title, children }: { title?: string; children: ReactNode }) => (
  <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm mb-6 last:mb-0">
    {title && (
      <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">{title}</h3>
    )}
    {children}
  </div>
);

const PolicySection = ({ id, title, children }: { id?: string; title: string; children: ReactNode }) => (
  <section id={id} className="scroll-mt-24">
    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">{title}</h2>
    {children}
  </section>
);

const ContactEmail = () => (
  <CopyableEmail email={CONTACT_EMAIL} href={`mailto:${CONTACT_EMAIL}`} className="text-genie-primary font-medium" />
);

const ExternalLink = ({ href, children }: { href: string; children: ReactNode }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 font-medium">
    {children}
  </a>
);

const PrivacyPolicy = () => {
  const lastUpdated = "October 28, 2026";

  const seoConfig = {
    title: "Privacy Policy | Help Genie",
    description: "Learn how Help Genie protects and handles your data. Our comprehensive privacy policy explains data collection, usage, sharing, and your rights.",
    keywords: ["privacy policy", "data protection", "privacy rights", "google user data", "help genie"],
    ogTitle: "Privacy Policy | Help Genie",
    ogDescription: "Your privacy matters. Learn how Help Genie protects and handles your data.",
    ogImage: "/insights-background.webp",
    canonicalUrl: getCanonicalUrl("/privacy"),
  };

  const listClass = "space-y-3 text-gray-700 text-base sm:text-lg";
  const paragraphClass = "text-gray-700 text-base sm:text-lg";

  return (
    <PublicPageTemplate seo={seoConfig}>
      <section className="relative w-full min-h-[400px] flex items-center justify-center text-white">
        <div className="absolute inset-0 z-0">
          <img src="/insights-background.webp" alt="Privacy Policy background" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-purple-900/80 to-blue-900/80 z-10"></div>
        </div>

        <div className="relative z-20 w-full max-w-screen-xl mx-auto px-8 py-20">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              Privacy Policy
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto">
              Your privacy matters. Learn how we protect and handle your data.
            </p>
            <p className="text-lg text-gray-300 mt-4">
              Last Updated: <span className="font-semibold">{lastUpdated}</span>
            </p>
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white shadow-lg">
            <CardContent className="pt-8 pb-8 px-6 sm:px-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Overview</h2>
              <p className="text-gray-700 leading-relaxed text-lg">
                Help Genie ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy
                explains how we collect, use, share, disclose, and safeguard your information when you use our AI
                agent platform and related services. Please read this privacy policy carefully.
              </p>
            </CardContent>
          </Card>

          <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white shadow-lg">
            <CardContent className="pt-8 pb-8 px-6 sm:px-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Google API Limited Use Disclosure</h2>
              <p className="text-gray-700 leading-relaxed text-lg">
                Help Genie's use and transfer of information received from Google APIs to any other app will adhere to the{' '}
                <ExternalLink href="https://developers.google.com/terms/api-services-user-data-policy">
                  Google API Services User Data Policy
                </ExternalLink>
                , including the Limited Use requirements. The use of raw or derived user data received from Google
                Workspace APIs will adhere to the{' '}
                <ExternalLink href="https://developers.google.com/workspace/workspace-api-user-data-developer-policy">
                  Google Workspace API User Data and Developer Policy
                </ExternalLink>
                , including the Limited Use requirements. We do not use Google user data to develop, improve, or train
                generalized or non-personalized AI and/or machine learning models. See{' '}
                <a href="#google-user-data" className="text-purple-600 hover:text-purple-700 font-medium">
                  Section 4
                </a>{' '}
                for details.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="w-full py-12 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            <PolicySection title="1. Information We Collect">
              <PolicyCard title="1.1 Information You Provide">
                <ul className={listClass}>
                  <Bullet><strong>Account Information:</strong> Name, email address, phone number, password, and billing information</Bullet>
                  <Bullet><strong>Agent Configuration:</strong> Agent names, prompts, knowledge base documents, and custom settings</Bullet>
                  <Bullet><strong>Uploaded Content:</strong> Documents, PDFs, and other files you upload to the knowledge base</Bullet>
                  <Bullet><strong>Communications:</strong> Messages, feedback, and support requests you send to us</Bullet>
                </ul>
              </PolicyCard>

              <PolicyCard title="1.2 Automatically Collected Information">
                <ul className={listClass}>
                  <Bullet><strong>Conversation Data:</strong> Voice recordings, transcripts, and metadata from interactions with your AI agents</Bullet>
                  <Bullet><strong>Usage Data:</strong> Pages viewed, features used, time spent, and interaction patterns</Bullet>
                  <Bullet><strong>Device Information:</strong> IP address, browser type, operating system, and device identifiers</Bullet>
                  <Bullet><strong>Analytics Data:</strong> Performance metrics, error logs, and system diagnostics</Bullet>
                </ul>
              </PolicyCard>

              <PolicyCard title="1.3 Information From Connected Accounts">
                <p className={paragraphClass}>
                  If you choose to connect a Google account (for example, to let your genie's notetaker join your
                  calendar meetings), we receive the limited Google user data described in{' '}
                  <a href="#google-user-data" className="text-purple-600 hover:text-purple-700 font-medium">
                    Section 4
                  </a>
                  . Connecting an account is optional, and you can disconnect it at any time.
                </p>
              </PolicyCard>
            </PolicySection>

            <PolicySection title="2. How We Use Your Information">
              <PolicyCard>
                <p className={`${paragraphClass} mb-4`}>We use the information we collect only to:</p>
                <ul className={listClass}>
                  <Bullet>Provide, operate, and maintain the features you use</Bullet>
                  <Bullet>Process transactions and manage your subscription</Bullet>
                  <Bullet>Send administrative information, service updates, and security alerts</Bullet>
                  <Bullet>Respond to your inquiries and provide customer support</Bullet>
                  <Bullet>Detect, prevent, and address technical issues, abuse, or security threats</Bullet>
                  <Bullet>Comply with legal obligations and enforce our Terms of Service</Bullet>
                </ul>
                <p className={`${paragraphClass} mt-4`}>
                  We do not sell your information, we do not use it for advertising, and we do not use your content
                  or conversation data to train generalized AI or machine learning models.
                </p>
              </PolicyCard>
            </PolicySection>

            <PolicySection title="3. How We Share and Disclose Information">
              <PolicyCard>
                <p className={`${paragraphClass} mb-4`}>
                  We do not sell, rent, or trade your personal information. We share or disclose it only in these cases:
                </p>
                <ul className={listClass}>
                  <Bullet>
                    <strong>Service providers:</strong> With the subprocessors listed in{' '}
                    <a href="#service-providers" className="text-purple-600 hover:text-purple-700 font-medium">
                      Section 5
                    </a>
                    , solely to operate the features you use, under contracts and terms that restrict them from using
                    your data for their own purposes
                  </Bullet>
                  <Bullet>
                    <strong>At your direction:</strong> When you ask us to, for example by sending meeting notes to an
                    email address you choose or connecting a third-party integration
                  </Bullet>
                  <Bullet>
                    <strong>Legal requirements:</strong> When required by law, regulation, legal process, or an
                    enforceable governmental request
                  </Bullet>
                  <Bullet>
                    <strong>Safety and security:</strong> To protect the rights, property, or safety of our users, the
                    public, or Help Genie, including to investigate fraud or abuse
                  </Bullet>
                  <Bullet>
                    <strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets,
                    in which case your information remains subject to this Privacy Policy and you will be notified
                  </Bullet>
                </ul>
              </PolicyCard>
            </PolicySection>

            <PolicySection id="google-user-data" title="4. Google User Data">
              <PolicyCard title="4.1 Data We Access">
                <p className={`${paragraphClass} mb-4`}>
                  When you connect your Google Calendar to a genie, we request read-only access with these scopes:
                </p>
                <ul className={listClass}>
                  <Bullet>
                    <strong>Calendar list</strong> (<code>calendar.readonly</code>): the names and IDs of your calendars,
                    so you can choose which calendar your genie watches
                  </Bullet>
                  <Bullet>
                    <strong>Calendar events</strong> (<code>calendar.events.readonly</code>): event titles, times,
                    video meeting links, and the organizer and attendee details of events on the calendars you
                    choose, so your genie's notetaker can join the meetings you enable
                  </Bullet>
                  <Bullet>
                    <strong>Basic profile</strong> (<code>openid</code>, <code>userinfo.email</code>): your Google
                    email address, to identify which account is connected
                  </Bullet>
                </ul>
                <p className={`${paragraphClass} mt-4`}>
                  We never create, change, or delete anything in your Google Calendar.
                </p>
              </PolicyCard>

              <PolicyCard title="4.2 How We Use Google User Data">
                <p className={`${paragraphClass} mb-4`}>
                  Google user data is used only to provide and improve the user-facing features you have enabled:
                </p>
                <ul className={listClass}>
                  <Bullet>Showing your calendars and upcoming meetings inside Help Genie</Bullet>
                  <Bullet>Sending your genie's notetaker to the meetings you choose, at the scheduled time</Bullet>
                  <Bullet>Producing a transcript, summary, and action items for those meetings and delivering them back to you</Bullet>
                </ul>
                <p className={`${paragraphClass} mt-4 mb-4`}>We do not:</p>
                <ul className={listClass}>
                  <Bullet>Sell Google user data or transfer it to data brokers or information resellers</Bullet>
                  <Bullet>Use or transfer Google user data for advertising, including retargeting, personalized, or interest-based ads</Bullet>
                  <Bullet>Use Google user data to determine creditworthiness or for lending purposes</Bullet>
                  <Bullet>
                    Use or transfer raw, aggregated, anonymized, or derived Google user data to develop, improve, or
                    train generalized or non-personalized AI or machine learning models, our own or anyone else's
                  </Bullet>
                  <Bullet>
                    Allow humans to read Google user data, except with your explicit consent (for example, when you ask
                    our support team for help), when necessary for security purposes such as investigating abuse,
                    or to comply with applicable law
                  </Bullet>
                </ul>
              </PolicyCard>

              <PolicyCard title="4.3 With Whom We Share Google User Data">
                <p className={`${paragraphClass} mb-4`}>
                  Google user data is shared only with the following subprocessors, and only to deliver the features
                  above:
                </p>
                <ul className={listClass}>
                  <Bullet>
                    <strong>Nylas:</strong> Securely holds the calendar authorization, reads the calendars and events
                    you select, and runs the meeting notetaker
                  </Bullet>
                  <Bullet>
                    <strong>Supabase:</strong> Encrypted database and file storage for meeting details, transcripts, and notes
                  </Bullet>
                  <Bullet>
                    <strong>OpenAI, Anthropic, Google Gemini, and OpenRouter:</strong> AI processing that turns a
                    meeting transcript into a summary, action items, and answers to your questions about it.
                    These providers are used through their paid business APIs, whose terms prohibit using our API
                    data to train their models. Requests through OpenRouter are restricted to model providers that
                    do not retain or train on prompts
                  </Bullet>
                </ul>
                <p className={`${paragraphClass} mt-4`}>
                  Each subprocessor may use the data only to provide its service to us. None of them may use it for
                  advertising or to train generalized AI or machine learning models.
                </p>
              </PolicyCard>

              <PolicyCard title="4.4 Retention, Revocation, and Deletion">
                <ul className={listClass}>
                  <Bullet>
                    You can disconnect your calendar at any time from your genie's meeting settings. Disconnecting
                    deletes the stored authorization immediately, and we stop accessing your calendar
                  </Bullet>
                  <Bullet>
                    You can also revoke access at any time from your{' '}
                    <ExternalLink href="https://myaccount.google.com/permissions">Google Account permissions page</ExternalLink>
                  </Bullet>
                  <Bullet>
                    Meeting notes and transcripts stay in your account until you delete them. You can delete them
                    yourself at any time, or ask us to by emailing <ContactEmail />
                  </Bullet>
                  <Bullet>
                    When you delete your account, all Google user data and data derived from it is deleted within 30 days
                  </Bullet>
                </ul>
              </PolicyCard>
            </PolicySection>

            <PolicySection id="service-providers" title="5. Third-Party Service Providers">
              <PolicyCard>
                <p className={`${paragraphClass} mb-4`}>
                  We use the following service providers to operate our platform. Each one processes data only on our
                  behalf and under its own privacy terms:
                </p>
                <ul className={listClass}>
                  <Bullet><strong>Supabase:</strong> Database, authentication, and file storage</Bullet>
                  <Bullet><strong>Nylas:</strong> Calendar connection and meeting notetaker</Bullet>
                  <Bullet><strong>OpenAI:</strong> AI analysis and natural language processing</Bullet>
                  <Bullet><strong>Anthropic:</strong> AI assistant and natural language processing</Bullet>
                  <Bullet><strong>Google Gemini:</strong> AI summaries of account activity</Bullet>
                  <Bullet><strong>OpenRouter:</strong> AI processing for summaries and reports</Bullet>
                  <Bullet><strong>ElevenLabs:</strong> Voice generation and conversational AI for phone and web calls</Bullet>
                  <Bullet><strong>Twilio:</strong> Phone number provisioning and call handling</Bullet>
                  <Bullet><strong>Stripe:</strong> Payment processing and subscription management</Bullet>
                </ul>
                <p className="text-gray-600 mt-4">
                  We use AI providers through paid API access under terms that prohibit them from training their
                  models on the data we send.
                </p>
              </PolicyCard>
            </PolicySection>

            <PolicySection title="6. Data Storage and Security">
              <PolicyCard>
                <p className={`${paragraphClass} mb-4`}>
                  We implement appropriate technical and organizational security measures to protect your information:
                </p>
                <ul className={listClass}>
                  <Bullet>Encryption in transit (TLS/SSL) and at rest</Bullet>
                  <Bullet>Regular security audits and vulnerability assessments</Bullet>
                  <Bullet>Access controls and authentication mechanisms, with staff access limited to what is strictly necessary</Bullet>
                  <Bullet>Automated backups and disaster recovery procedures</Bullet>
                </ul>
                <p className="text-gray-600 mt-4">
                  However, no method of transmission over the Internet or electronic storage is 100% secure.
                  While we strive to protect your information, we cannot guarantee its absolute security.
                </p>
              </PolicyCard>
            </PolicySection>

            <PolicySection title="7. Data Retention">
              <PolicyCard>
                <p className={`${paragraphClass} mb-4`}>We retain your information for as long as necessary to:</p>
                <ul className={listClass}>
                  <Bullet>Provide our services and maintain your account</Bullet>
                  <Bullet>Comply with legal, tax, and accounting obligations</Bullet>
                  <Bullet>Resolve disputes and enforce our agreements</Bullet>
                </ul>
                <p className="text-gray-600 mt-4">
                  When you delete your account, we will delete or anonymize your personal information within 30 days,
                  except where we are required to retain it for legal purposes.
                </p>
              </PolicyCard>
            </PolicySection>

            <PolicySection title="8. Your Rights and Choices">
              <PolicyCard>
                <p className={`${paragraphClass} mb-4`}>
                  Depending on your location, you may have the following rights:
                </p>
                <ul className={listClass}>
                  <Bullet><strong>Access:</strong> Request a copy of the personal information we hold about you</Bullet>
                  <Bullet><strong>Correction:</strong> Update or correct inaccurate information</Bullet>
                  <Bullet><strong>Deletion:</strong> Request deletion of your personal information</Bullet>
                  <Bullet><strong>Portability:</strong> Receive your data in a structured, machine-readable format</Bullet>
                  <Bullet><strong>Objection:</strong> Object to certain processing of your information</Bullet>
                  <Bullet><strong>Restriction:</strong> Request restriction of processing in certain circumstances</Bullet>
                  <Bullet><strong>Withdraw consent:</strong> Disconnect any connected account, such as Google Calendar, at any time</Bullet>
                </ul>
                <p className="text-gray-600 mt-4">
                  To exercise these rights, please contact us at <ContactEmail />
                </p>
              </PolicyCard>
            </PolicySection>

            <PolicySection title="9. Cookies and Tracking Technologies">
              <PolicyCard>
                <p className={paragraphClass}>
                  We use cookies and similar tracking technologies to track activity on our platform and store certain information.
                  For more details, please see our{' '}
                  <a href="/cookies" className="text-purple-600 hover:text-purple-700 font-medium">
                    Cookie Policy
                  </a>.
                </p>
              </PolicyCard>
            </PolicySection>

            <PolicySection title="10. Children's Privacy">
              <PolicyCard>
                <p className={paragraphClass}>
                  Our services are not intended for children under 13 years of age. We do not knowingly collect
                  personal information from children under 13. If you believe we have collected information from
                  a child under 13, please contact us immediately.
                </p>
              </PolicyCard>
            </PolicySection>

            <PolicySection title="11. International Data Transfers">
              <PolicyCard>
                <p className={paragraphClass}>
                  Your information may be transferred to and processed in countries other than your country of residence.
                  These countries may have data protection laws that differ from those of your country. We ensure that
                  appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
                </p>
              </PolicyCard>
            </PolicySection>

            <PolicySection title="12. Changes to This Privacy Policy">
              <PolicyCard>
                <p className={paragraphClass}>
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting
                  the new Privacy Policy on this page and updating the "Last Updated" date. We encourage you to review
                  this Privacy Policy periodically for any changes.
                </p>
              </PolicyCard>
            </PolicySection>

            <PolicySection title="13. Contact Us">
              <PolicyCard>
                <p className={`${paragraphClass} mb-4`}>
                  If you have any questions about this Privacy Policy, please contact us:
                </p>
                <ul className="space-y-2 text-gray-700 text-base sm:text-lg">
                  <Bullet>By email: <ContactEmail /></Bullet>
                  <Bullet>
                    By visiting our support page:{' '}
                    <a href="https://helpgenie.ai/contact" className="text-purple-600 hover:text-purple-700 font-medium">
                      helpgenie.ai/contact
                    </a>
                  </Bullet>
                </ul>
              </PolicyCard>
            </PolicySection>
          </div>
        </div>
      </section>

      <GenieEmbed position="bottom-right" />
    </PublicPageTemplate>
  );
};

export default PrivacyPolicy;
